import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Scissors, Shirt, Users, Sparkles, Instagram, Youtube, Facebook, Smartphone } from "lucide-react";
import axios from "axios";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" },
  }),
};

const pillars = [];

export default function HomePage() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/blog/posts`)
      .then((r) => setPosts(r.data.slice(0, 3)))
      .catch(console.error);
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_high-on-life-1/artifacts/ay91all1_633B6531-3265-405F-B332-7637D4004508-308-000000165637292E.jpg"
            alt="Jax watching sunset on Cornwall beach"
            className="w-full h-full object-cover object-[center_35%]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
          <p className="font-accent text-2xl md:text-3xl text-white/80 mb-4">
            living the good life in Cornwall
          </p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight leading-[1.1] max-w-3xl">
            Be the Vibe
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
            Welcome to Justjjax — where hair, wellness, style and community come together.
            Created by Jax, rooted in Cornwall.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="https://tinyurl.com/2nxodcgu"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-olive text-white hover:bg-amber rounded-full px-8 py-3.5 font-medium transition-all duration-300 hover:-translate-y-1 shadow-lg"
              data-testid="hero-explore-btn"
            >
              Book an Appointment
            </a>
            <Link
              to="/community"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-black rounded-full px-8 py-3.5 font-medium transition-all duration-300"
              data-testid="hero-book-btn"
            >
              Book a Ticket to an Event
            </Link>
            <Link
              to="/curlscan"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-black rounded-full px-8 py-3.5 font-medium transition-all duration-300 inline-flex items-center gap-2"
              data-testid="hero-curl-edit-btn"
            >
              <Smartphone size={18} /> Download The Curl Edit
            </Link>
          </div>
        </div>
      </section>

      {/* Jax Intro */}
      <section className="py-16 md:py-20 bg-warmcream" data-testid="jax-intro">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_high-on-life-1/artifacts/vbifw6nb_166D9C66-50FC-4D44-8BFD-C19C228737AB-308-000000061EF9CC8B.jpg"
                alt="Jax relaxing by the coast"
                className="rounded-3xl w-full aspect-[4/5] object-cover object-top"
              />
            </div>
            <div>
              <p className="font-accent text-2xl text-sunset mb-3">hi, I'm Jax</p>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-6">
                Hairstylist. Wellness Enthusiast. Community Builder.
              </h2>
              <p className="text-charcoal/70 leading-relaxed mb-6">
                I'm a Cornwall-based creative who believes life should be about finding joy in what we do,
                what we wear, what we listen to and how we connect. Welcome — you're exactly where you need to be.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-sunset font-medium hover:text-amber transition-colors"
                data-testid="jax-intro-link"
              >
                Read my story <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Follow the Journey - Social Embeds */}
      <section className="py-16 md:py-20 bg-sage/5" data-testid="live-socials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
            <p className="font-display text-xl">Follow the journey live</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Instagram - Live Elfsight Feed */}
            <div className="bg-white rounded-3xl overflow-hidden border border-ocean/5 hover:shadow-lg transition-all" data-testid="instagram-embed">
              <div className="p-5 flex items-center justify-between border-b border-ocean/5">
                <div className="flex items-center gap-3">
                  <Instagram size={20} className="text-sunset" />
                  <div>
                    <p className="font-medium text-sm">@justjjax</p>
                    <p className="text-xs text-charcoal/40">Instagram</p>
                  </div>
                </div>
                <a
                  href="https://www.instagram.com/justjjax/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-olive text-white text-xs font-medium px-4 py-1.5 rounded-full hover:bg-amber transition-colors"
                >
                  Follow
                </a>
              </div>
              <div className="p-2">
                <div className="elfsight-app-c551c6a8-884b-48bd-8c9f-99586f168051" data-elfsight-app-lazy></div>
              </div>
            </div>

            {/* TikTok - Live Elfsight Feed */}
            <div className="bg-white rounded-3xl overflow-hidden border border-ocean/5 hover:shadow-lg transition-all" data-testid="tiktok-embed">
              <div className="p-5 flex items-center justify-between border-b border-ocean/5">
                <div className="flex items-center gap-3">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-charcoal"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.88-2.88 2.89 2.89 0 012.88-2.88c.3 0 .59.05.86.12V9.01a6.32 6.32 0 00-.86-.06 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.75a8.28 8.28 0 004.76 1.5V6.8a4.84 4.84 0 01-1-.11z"/></svg>
                  <div>
                    <p className="font-medium text-sm">@Just_jjax</p>
                    <p className="text-xs text-charcoal/40">TikTok</p>
                  </div>
                </div>
                <a
                  href="https://tiktok.com/@Just_jjax"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-charcoal text-white text-xs font-medium px-4 py-1.5 rounded-full hover:bg-eucalyptus transition-colors"
                >
                  Follow
                </a>
              </div>
              <div className="p-2">
                <div className="elfsight-app-452500c2-e110-4ad0-922a-b95d4f4aafa0" data-elfsight-app-lazy></div>
              </div>
            </div>
          </div>

          {/* Other socials row */}
          <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
            <a
              href="https://www.instagram.com/lazyelley/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white rounded-full px-5 py-2.5 text-sm font-medium text-charcoal hover:text-sunset hover:shadow-md transition-all"
              data-testid="live-lazyelley"
            >
              <Instagram size={16} /> @lazyelley
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61550018370626"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white rounded-full px-5 py-2.5 text-sm font-medium text-charcoal hover:text-sunset hover:shadow-md transition-all"
              data-testid="live-facebook"
            >
              <Facebook size={16} /> Facebook
            </a>
            <span
              className="flex items-center gap-2 bg-white/60 rounded-full px-5 py-2.5 text-sm font-medium text-charcoal/50 cursor-default"
              data-testid="live-youtube"
              title="YouTube — coming soon"
            >
              <Youtube size={16} /> YouTube · Coming soon
            </span>
          </div>

          {/* The Curl Edit app preview */}
          <div className="mt-12 bg-white rounded-3xl border border-ocean/5 overflow-hidden grid md:grid-cols-2 items-center" data-testid="home-curl-edit-preview">
            <div className="p-8 md:p-10">
              <span className="inline-flex items-center gap-2 bg-amber/10 text-amber text-xs font-medium tracking-wider uppercase px-4 py-1.5 rounded-full mb-4">
                <Sparkles size={13} /> Coming Soon
              </span>
              <h3 className="font-display text-2xl sm:text-3xl text-black mb-3">The Curl Edit App</h3>
              <p className="text-charcoal/70 text-sm leading-relaxed mb-6 max-w-sm">
                Snap a photo, discover your curl type and get a personalised routine — coming soon to
                your pocket.
              </p>
              <Link
                to="/curlscan"
                className="bg-olive text-white hover:bg-amber rounded-full px-6 py-2.5 text-sm font-medium transition-all inline-flex items-center gap-2"
                data-testid="home-curl-edit-cta"
              >
                <Smartphone size={16} /> Join the waitlist
              </Link>
            </div>
            <div className="flex justify-center p-6 bg-gradient-to-br from-sage/10 to-peachlight">
              <img
                src="/curl-edit-preview.jpg"
                alt="The Curl Edit app preview"
                className="max-h-[360px] w-auto object-contain drop-shadow-xl rounded-2xl"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Latest from the Blog — hidden for now (blog coming soon) */}
      {false && posts.length > 0 && (
        <section className="py-24 md:py-32 bg-peachlight" data-testid="latest-blog-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="font-accent text-xl text-sunset mb-2">from the blog</p>
                <h2 className="font-display text-3xl sm:text-4xl tracking-tight">High on Life</h2>
              </div>
              <Link
                to="/high-on-life"
                className="hidden md:flex items-center gap-2 text-sm font-medium text-charcoal hover:text-black transition-colors"
                data-testid="view-all-posts"
              >
                View all posts <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {posts.map((post, i) => (
                <motion.div
                  key={post.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                >
                  <Link
                    to={`/high-on-life/${post.id}`}
                    className="group block"
                    data-testid={`blog-card-${i}`}
                  >
                    <div className="relative overflow-hidden rounded-2xl aspect-[4/3] mb-4">
                      <img
                        src={post.image_url}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/90 backdrop-blur-sm text-black text-xs font-medium px-3 py-1 rounded-full capitalize">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    <h3 className="font-display text-lg group-hover:text-sage transition-colors">
                      {post.title}
                    </h3>
                    <p className="mt-2 text-charcoal/60 text-sm line-clamp-2">{post.excerpt}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
            <Link
              to="/high-on-life"
              className="md:hidden flex items-center gap-2 text-sm font-medium text-charcoal mt-8"
              data-testid="view-all-posts-mobile"
            >
              View all posts <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      )}

    </div>
  );
}
